import { create } from 'zustand'
import { 
  DashboardState, 
  StrategyEntry, 
  RunMetadata, 
  ResearchTabState, 
  ValidationTabState, 
  LiveSignalTabState 
} from '../types'

interface DashboardStore extends DashboardState {
  // Data
  strategies: StrategyEntry[]
  runs: RunMetadata[]
  
  // Tab states
  researchTab: ResearchTabState
  validationTab: ValidationTabState
  liveSignalTab: LiveSignalTabState
  
  // Actions
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  setConnectionStatus: (status: 'connected' | 'disconnected' | 'connecting') => void
  setLastUpdate: (timestamp: string) => void
  
  // Data actions
  setStrategies: (strategies: StrategyEntry[]) => void
  addStrategy: (strategy: StrategyEntry) => void
  setRuns: (runs: RunMetadata[]) => void
  addRun: (run: RunMetadata) => void
  
  // Research tab actions
  setSelectedRun: (runId: string | null) => void
  setTimeRange: (range: 'all' | '24h' | '7d' | '30d') => void
  setShowSurvivorsOnly: (show: boolean) => void
  setSelectedMetric: (metric: 'dsr' | 'return' | 'drawdown') => void
  
  // Validation tab actions
  setSelectedStrategy: (strategyId: string | null) => void
  setSplitDate: (date: string | null) => void
  setShowDegradation: (show: boolean) => void
  
  // Live signal tab actions
  setSelectedBook: (bookId: string | null) => void
  setCredibilityThreshold: (threshold: number) => void
  setRealTimeMode: (enabled: boolean) => void
}

export const useDashboardStore = create<DashboardStore>((set, get) => ({
  // Initial state
  isLoading: false,
  error: null,
  lastUpdate: null,
  connectionStatus: 'disconnected',
  
  // Data
  strategies: [],
  runs: [],
  
  // Tab states
  researchTab: {
    selectedRun: null,
    timeRange: 'all',
    showSurvivorsOnly: false,
    selectedMetric: 'dsr'
  },
  
  validationTab: {
    selectedStrategy: null,
    splitDate: null,
    showDegradation: true
  },
  
  liveSignalTab: {
    selectedBook: null,
    credibilityThreshold: 0.7,
    realTimeMode: false
  },
  
  // Basic actions
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  setConnectionStatus: (status) => set({ connectionStatus: status }),
  setLastUpdate: (timestamp) => set({ lastUpdate: timestamp }),
  
  // Data actions
  setStrategies: (strategies) => set({ strategies }),
  addStrategy: (strategy) => set((state) => ({ 
    strategies: [...state.strategies, strategy] 
  })),
  
  setRuns: (runs) => set({ runs }),
  addRun: (run) => set((state) => ({ 
    runs: [...state.runs, run] 
  })),
  
  // Research tab actions
  setSelectedRun: (runId) => set((state) => ({
    researchTab: { ...state.researchTab, selectedRun: runId }
  })),
  
  setTimeRange: (range) => set((state) => ({
    researchTab: { ...state.researchTab, timeRange: range }
  })),
  
  setShowSurvivorsOnly: (show) => set((state) => ({
    researchTab: { ...state.researchTab, showSurvivorsOnly: show }
  })),
  
  setSelectedMetric: (metric) => set((state) => ({
    researchTab: { ...state.researchTab, selectedMetric: metric }
  })),
  
  // Validation tab actions
  setSelectedStrategy: (strategyId) => set((state) => ({
    validationTab: { ...state.validationTab, selectedStrategy: strategyId }
  })),
  
  setSplitDate: (date) => set((state) => ({
    validationTab: { ...state.validationTab, splitDate: date }
  })),
  
  setShowDegradation: (show) => set((state) => ({
    validationTab: { ...state.validationTab, showDegradation: show }
  })),
  
  // Live signal tab actions
  setSelectedBook: (bookId) => set((state) => ({
    liveSignalTab: { ...state.liveSignalTab, selectedBook: bookId }
  })),
  
  setCredibilityThreshold: (threshold) => set((state) => ({
    liveSignalTab: { ...state.liveSignalTab, credibilityThreshold: threshold }
  })),
  
  setRealTimeMode: (enabled) => set((state) => ({
    liveSignalTab: { ...state.liveSignalTab, realTimeMode: enabled }
  }))
}))

// Selectors for computed values
export const useStrategyStats = () => {
  const strategies = useDashboardStore((state) => state.strategies)
  
  const totalStrategies = strategies.length
  const survivors = strategies.filter(s => s.is_survivor)
  const totalSurvivors = survivors.length
  const survivalRate = totalStrategies > 0 ? totalSurvivors / totalStrategies : 0
  
  const dsrValues = strategies
    .filter(s => s.deflated_sharpe_ratio !== null)
    .map(s => s.deflated_sharpe_ratio!)
  
  const bestDsr = dsrValues.length > 0 ? Math.max(...dsrValues) : 0
  const meanDsr = dsrValues.length > 0 ? dsrValues.reduce((a, b) => a + b, 0) / dsrValues.length : 0
  
  return {
    totalStrategies,
    totalSurvivors,
    survivalRate,
    bestDsr,
    meanDsr,
    survivors
  }
}

export const useFilteredStrategies = () => {
  const strategies = useDashboardStore((state) => state.strategies)
  const researchTab = useDashboardStore((state) => state.researchTab)
  
  let filtered = strategies
  
  // Filter by selected run
  if (researchTab.selectedRun) {
    filtered = filtered.filter(s => s.run_id === researchTab.selectedRun)
  }
  
  // Filter by survivors only
  if (researchTab.showSurvivorsOnly) {
    filtered = filtered.filter(s => s.is_survivor)
  }
  
  // Filter by time range
  if (researchTab.timeRange !== 'all') {
    const now = new Date()
    let cutoffDate: Date
    
    switch (researchTab.timeRange) {
      case '24h':
        cutoffDate = new Date(now.getTime() - 24 * 60 * 60 * 1000)
        break
      case '7d':
        cutoffDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        break
      case '30d':
        cutoffDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
        break
      default:
        cutoffDate = new Date(0)
    }
    
    filtered = filtered.filter(s => new Date(s.timestamp) >= cutoffDate)
  }
  
  return filtered
}

export const useActiveRuns = () => {
  const runs = useDashboardStore((state) => state.runs)
  return runs.filter(run => !run.ended_at)
}

export const useCompletedRuns = () => {
  const runs = useDashboardStore((state) => state.runs)
  return runs.filter(run => run.ended_at)
}